import React, { useEffect, useState } from "react";
import {
  SideSheet, Card, Button, Timeline, Modal, Spin, Typography,
} from "@douyinfe/semi-ui";
import { ManualApi, ManualVersionInfo } from "./api";

const { Text } = Typography;

interface Props {
  visible: boolean;
  manualId: number | null;
  onClose: () => void;
  onRollbackDone: () => void;
}

export function ManualVersionDrawer({ visible, manualId, onClose, onRollbackDone }: Props) {
  const [versions, setVersions] = useState<ManualVersionInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState("");

  const load = async () => {
    if (!manualId) return;
    setLoading(true);
    try {
      const resp = await ManualApi.versions(manualId);
      setVersions(resp || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && manualId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, manualId]);

  const onPreview = async (vno: number) => {
    if (!manualId) return;
    const resp = await ManualApi.versionContent(manualId, vno);
    const html = resp?.htmlContent || "";
    const doc = `<!DOCTYPE html><html><head><meta charset="UTF-8"><link href="/api/mis/manual/theme.css" rel="stylesheet"></head><body>${html}</body></html>`;
    setPreviewHtml(doc);
    setPreviewTitle(`版本 v${vno} 预览`);
  };

  const onRollback = (vno: number) => {
    if (!manualId) return;
    Modal.confirm({
      title: `确认回滚到 v${vno}?`,
      content: "当前版本会自动归档保存，不会丢失。",
      onOk: async () => {
        await ManualApi.rollback(manualId, vno);
        onRollbackDone();
        onClose();
      },
    });
  };

  return (
    <>
      <SideSheet
        title="历史版本"
        visible={visible}
        onCancel={onClose}
        width={480}
      >
        <Spin spinning={loading}>
          <Timeline>
            {versions.map((v) => (
              <Timeline.Item key={v.id} time={v.createdAt}>
                <Card style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text strong>v{v.versionNo}</Text>
                    <Text type="tertiary" size="small">创建人 #{v.createdBy}</Text>
                  </div>
                  {v.changeNote && (
                    <div>
                      <Text type="tertiary" size="small">{v.changeNote}</Text>
                    </div>
                  )}
                  <div style={{ marginTop: 8 }}>
                    <Button size="small" onClick={() => onPreview(v.versionNo)}>预览</Button>
                    {v.versionNo !== versions[0]?.versionNo && (
                      <Button
                        size="small"
                        type="warning"
                        style={{ marginLeft: 8 }}
                        onClick={() => onRollback(v.versionNo)}
                      >
                        回滚
                      </Button>
                    )}
                  </div>
                </Card>
              </Timeline.Item>
            ))}
          </Timeline>
        </Spin>
      </SideSheet>

      {previewHtml && (
        <SideSheet
          title={previewTitle}
          visible={!!previewHtml}
          onCancel={() => setPreviewHtml(null)}
          width="70%"
          bodyStyle={{ padding: 0 }}
        >
          <iframe
            srcDoc={previewHtml}
            style={{ width: "100%", height: "calc(100vh - 56px)", border: "none" }}
            title="版本预览"
          />
        </SideSheet>
      )}
    </>
  );
}
